import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import {
  CreateChartOfAccountDialog,
  type CreateChartContext,
} from "@/components/dataBank/CreateChartOfAccountDialog";
import { ChartOfAccountLedgerDialog } from "@/components/dataBank/ChartOfAccountLedgerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { can, getStoredAuthUser } from "@/lib/authUser";
import {
  collectLeafAccountIds,
  deleteChartOfAccountNode,
  fetchChartOfAccountsTree,
  filterChartTree,
  flattenChartTree,
  parseChartNodeRef,
  uniqueAccountClasses,
  type ChartOfAccountNode,
} from "@/lib/dataBank/chartOfAccountsStore";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { BookOpen, ChevronDown, ChevronRight, Loader2, Plus, RotateCcw, Search, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

function accountNameClass(level: 1 | 2 | 3): string {
  if (level === 1) {
    return "font-bold text-teal-700 dark:text-teal-300";
  }
  if (level === 2) {
    return "font-semibold text-sky-700 dark:text-sky-300";
  }
  return "font-medium text-slate-700 dark:text-slate-200";
}

function formatBalance(amount: number, side: "Dr" | "Cr", symbol: string): string {
  const abs = Math.abs(amount);
  const formatted = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const prefix = symbol ? `${symbol} ` : "";
  return `${prefix}${formatted} ${side}.`;
}

function findParentType(tree: ChartOfAccountNode[], detailId: string): ChartOfAccountNode | null {
  for (const type of tree) {
    if (type.children.some((c) => c.id === detailId)) return type;
  }
  return null;
}

function findParentDetail(tree: ChartOfAccountNode[], leafId: string): ChartOfAccountNode | null {
  for (const type of tree) {
    for (const detail of type.children) {
      if (detail.children.some((c) => c.id === leafId)) return detail;
    }
  }
  return null;
}

const ChartOfAccounts = () => {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const [isForbidden, setIsForbidden] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tree, setTree] = useState<ChartOfAccountNode[]>([]);
  const [query, setQuery] = useState("");
  const [accountClass, setAccountClass] = useState("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [ledgerNode, setLedgerNode] = useState<ChartOfAccountNode | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createContext, setCreateContext] = useState<CreateChartContext | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChartOfAccountNode | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canView = can(user, "chart_of_account_can_view_any") || can(user, "bank_can_view_any");

  const currencySymbol = user?.company_branding?.currency?.symbol?.trim() || "$";

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const data = await fetchChartOfAccountsTree();
      setTree(data);
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        setIsForbidden(true);
        return;
      }
      console.error("[ChartOfAccounts] Load failed", error);
      toast.error("Could not load chart of accounts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("auth_user_updated", onAuth);
    return () => window.removeEventListener("auth_user_updated", onAuth);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const classes = useMemo(() => uniqueAccountClasses(tree), [tree]);
  const filtered = useMemo(() => filterChartTree(tree, query, accountClass), [tree, query, accountClass]);
  const rows = useMemo(() => flattenChartTree(filtered), [filtered]);
  const visibleRows = useMemo(() => {
    const hidden = new Set<string>();
    const isHidden = (node: ChartOfAccountNode, parentCollapsed: boolean): boolean => {
      if (parentCollapsed) hidden.add(node.id);
      const selfCollapsed = collapsed.has(node.id) || parentCollapsed;
      for (const child of node.children) {
        isHidden(child, selfCollapsed);
      }
      return selfCollapsed;
    };
    for (const node of filtered) isHidden(node, false);
    return rows.filter((row) => !hidden.has(row.id));
  }, [filtered, rows, collapsed]);

  const toggle = (id: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const ledgerAccountIds = useMemo(
    () => (ledgerNode ? collectLeafAccountIds(ledgerNode) : []),
    [ledgerNode],
  );

  const resetFilters = () => {
    setQuery("");
    setAccountClass("all");
  };

  const openLedger = (row: ChartOfAccountNode) => {
    if (collectLeafAccountIds(row).length === 0) return;
    setLedgerNode(row);
  };

  const openCreate = (ctx: CreateChartContext) => {
    setCreateContext(ctx);
    setCreateOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteChartOfAccountNode(deleteTarget.id);
      toast.success(`Deleted “${deleteTarget.name}”.`);
      setDeleteTarget(null);
      await load();
    } catch (error) {
      const message = isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : null;
      toast.error(message || "Could not delete account.");
    } finally {
      setDeleting(false);
    }
  };

  const openAddChild = (row: ChartOfAccountNode) => {
    const ref = parseChartNodeRef(row.id);
    if (!ref) return;

    if (row.level === 1) {
      openCreate({
        level: 2,
        parentTypeId: ref.numericId,
        parentTypeName: row.name,
        parentTypeCode: row.code,
      });
      return;
    }

    if (row.level === 2) {
      const parentType = findParentType(tree, row.id);
      openCreate({
        level: 3,
        parentTypeId: parentType ? parseChartNodeRef(parentType.id)?.numericId ?? null : null,
        parentTypeName: parentType?.name ?? null,
        parentDetailTypeId: ref.numericId,
        parentDetailName: row.name,
        parentDetailCode: row.code,
      });
      return;
    }

    // Level 3 → add sibling under same group
    const parentDetail = findParentDetail(tree, row.id);
    if (!parentDetail) return;
    const detailRef = parseChartNodeRef(parentDetail.id);
    const parentType = findParentType(tree, parentDetail.id);
    openCreate({
      level: 3,
      parentTypeId: parentType ? parseChartNodeRef(parentType.id)?.numericId ?? null : null,
      parentTypeName: parentType?.name ?? null,
      parentDetailTypeId: detailRef?.numericId ?? null,
      parentDetailName: parentDetail.name,
      parentDetailCode: parentDetail.code,
    });
  };

  if (!canView) {
    return (
      <PermissionDenied
        title="Chart of accounts access required"
        message="You don’t have permission to view chart of accounts. Ask an administrator to grant chart_of_account_can_view_any."
      />
    );
  }

  return isForbidden ? (
    <PermissionDenied
      title="Chart of accounts access required"
      message="You don’t have permission to view chart of accounts. Ask an administrator to grant chart_of_account_can_view_any."
    />
  ) : (
    <Layout title="Chart of Accounts">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Chart of Accounts</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Level 1 account types, level 2 groups, and level 3 accounts with balances.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          <Button
            type="button"
            className="h-10 gap-2 shrink-0"
            onClick={() => openCreate({ level: 1 })}
          >
            <Plus className="h-4 w-4" />
            Add account type
          </Button>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 min-w-0">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="glass-input h-10 pl-9"
              placeholder="Search account"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Select value={accountClass} onValueChange={setAccountClass}>
            <SelectTrigger className="h-10 w-full sm:w-56">
              <SelectValue placeholder="Select account class" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {classes.map((cls) => (
                <SelectItem key={cls} value={cls}>
                  {cls}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button type="button" variant="glass" className="h-10 gap-2" onClick={resetFilters}>
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center rounded-xl border border-border/50 bg-card/20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : visibleRows.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center rounded-xl border border-border/50 bg-card/20 p-6 text-center">
            <BookOpen className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <h3 className="font-semibold text-foreground">No accounts found</h3>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">
              {query || accountClass !== "all"
                ? "No accounts match your filters."
                : "No chart of accounts have been created for this company yet."}
            </p>
            {!query && accountClass === "all" ? (
              <Button type="button" className="mt-4 gap-2" onClick={() => openCreate({ level: 1 })}>
                <Plus className="h-4 w-4" />
                Add account type
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account Name</TableHead>
                  <TableHead>Account Code</TableHead>
                  <TableHead>Account Class</TableHead>
                  <TableHead>Account Group</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead className="w-[140px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => {
                  const indent = row.level === 1 ? 0 : row.level === 2 ? 16 : 36;
                  const hasChildren = row.children.length > 0;
                  const isOpen = !collapsed.has(row.id);
                  const addLabel =
                    row.level === 1 ? "Add group" : row.level === 2 ? "Add account" : "Add sibling";
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <div className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: indent }}>
                          {hasChildren ? (
                            <button
                              type="button"
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
                              onClick={() => toggle(row.id)}
                              aria-label={isOpen ? "Collapse" : "Expand"}
                            >
                              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          ) : (
                            <span className="inline-block w-6 shrink-0" />
                          )}
                          <span className={cn("truncate rounded-md px-2 py-0.5", accountNameClass(row.level))}>
                            {row.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{row.code || "—"}</TableCell>
                      <TableCell>{row.accountClass}</TableCell>
                      <TableCell>{row.level === 3 ? row.accountGroup || "N/A" : "N/A"}</TableCell>
                      <TableCell
                        className={cn(
                          "text-right tabular-nums",
                          row.level === 3 ? "font-medium text-foreground" : "text-muted-foreground",
                        )}
                      >
                        {collectLeafAccountIds(row).length > 0 ? (
                          <button
                            type="button"
                            className="inline-flex min-h-8 items-center justify-end rounded-md px-1.5 py-0.5 text-primary underline-offset-4 hover:bg-primary/10 hover:underline"
                            onClick={() => openLedger(row)}
                          >
                            {formatBalance(row.balance, row.balanceSide, currencySymbol)}
                          </button>
                        ) : (
                          <span className="inline-flex min-h-8 items-center justify-end px-1.5 py-0.5">
                            {formatBalance(row.balance, row.balanceSide, currencySymbol)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center justify-end gap-0.5">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 px-2 text-xs"
                            title={addLabel}
                            onClick={() => openAddChild(row)}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {row.level === 1 ? "Group" : row.level === 2 ? "Account" : "Sibling"}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 px-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            title="Delete"
                            onClick={() => setDeleteTarget(row)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <ChartOfAccountLedgerDialog
          open={ledgerNode != null}
          node={ledgerNode}
          accountIds={ledgerAccountIds}
          currencySymbol={currencySymbol}
          onOpenChange={(open) => {
            if (!open) setLedgerNode(null);
          }}
        />
        <CreateChartOfAccountDialog
          open={createOpen}
          context={createContext}
          tree={tree}
          onOpenChange={(open) => {
            setCreateOpen(open);
            if (!open) setCreateContext(null);
          }}
          onCreated={() => {
            void load();
          }}
        />
        <ConfirmAlertDialog
          open={deleteTarget != null}
          onOpenChange={(open) => {
            if (!open && !deleting) setDeleteTarget(null);
          }}
          title="Delete account?"
          description={
            deleteTarget ? (
              <>
                Delete <span className="font-semibold text-foreground">{deleteTarget.name}</span>
                {deleteTarget.code ? (
                  <>
                    {" "}
                    (<span className="font-mono text-xs">{deleteTarget.code}</span>)
                  </>
                ) : null}
                ? This only works if the account is not used in journals, banks, expenses, or purchase cost
                types
                {deleteTarget.level < 3 ? ", and has no child accounts" : ""}.
              </>
            ) : (
              "Delete this account?"
            )
          }
          confirmLabel={deleting ? "Deleting…" : "Delete"}
          onConfirm={(e) => {
            e.preventDefault();
            if (!deleting) void confirmDelete();
          }}
          variant="danger"
        />
      </div>
    </Layout>
  );
};

export default ChartOfAccounts;
